import React from "react";
import { CircularProgress } from "@mui/material";
import capiBg from "../../assets/images/svg/capi-bg.svg";
import facebookPixelIcon from "../../assets/images/svg/facebookPixel.svg";
import addStream from "../../assets/images/svg/save-capi.svg";
import PixelCapiHowTo from "./PixelCapiHowTo";

interface PixelCapiTrackingSectionProps {
  showIntegrationReady: boolean;
  capiTokenValue: string;
  onCapiTokenChange: (token: string) => void;
  capiTokenDisabled: boolean;
  isHowToOpen: boolean;
  onToggleHowTo: () => void;
  onSave: () => void;
  saveDisabled: boolean;
  isSaving: boolean;
}

export default function PixelCapiTrackingSection({
  showIntegrationReady,
  capiTokenValue,
  onCapiTokenChange,
  capiTokenDisabled,
  isHowToOpen,
  onToggleHowTo,
  onSave,
  saveDisabled,
  isSaving,
}: PixelCapiTrackingSectionProps) {
  const isSaveDisabled = saveDisabled || !capiTokenValue.trim();

  return (
    <div className="lg:col-span-2 w-full mt-1">
      <div className="relative w-full rounded-[20px] overflow-hidden lg:p-6 px-4 py-[24px] flex flex-col gap-[12px]">
        <img
          src={capiBg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />
        <div className="absolute inset-0 pointer-events-none" />

        {showIntegrationReady ? (
          <div className="relative flex items-start gap-3">
            <div className="shrink-0 lg:mt-[2px]">
              <img src={addStream} alt="Ready" className="w-[24px] h-[24px]" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-white text-[14px] lg:text-[16px] font-normal">
                Pixel &amp; CAPI Integration Ready
              </p>
              <p className="text-white text-[13px] lg:text-[14px]  lg:ml-0 ml-[-30px] mt-1 font-normal">
                Selected Pixel is configured with the CAPI token, and you're all
                set to launch your new campaign successfully!
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="relative z-10 flex items-start gap-[12px]">
              <div className="mt-[4px] lg:mt-[-2px] shrink-0">
                <img src={facebookPixelIcon} className="w-[24px] h-[24px]" />
              </div>
              <div className="flex flex-col gap-3">
                <h3 className="text-white text-[14px] lg:text-[16px] font-normal leading-[20px]">
                  Set up tracking for selected
                  <span className="block lg:inline"> pixel</span>
                </h3>
                <p className="hidden lg:block ml-[-32px] text-white text-[16px] font-normal">
                  Add a CAPI token to improve tracking accuracy and campaign
                  performance
                </p>
              </div>
            </div>
            <p className="lg:hidden w-full min-w-[260px] text-white text-[13px] font-normal">
              Add a CAPI token to improve tracking accuracy and campaign
              performance
            </p>
            <div className="relative z-10 flex flex-col gap-[12px]">
              <input
                type="text"
                id="capi-token"
                placeholder="CAPI token (optional)"
                value={capiTokenValue}
                onChange={(e) => onCapiTokenChange(e.target.value)}
                disabled={capiTokenDisabled}
                className={`w-full h-12 px-4 rounded-lg outline-none border transition-all text-sm ${
                  capiTokenDisabled
                    ? "bg-gray-400 text-black cursor-not-allowed"
                    : "bg-white text-black focus:ring-2 placeholder:text-[11.2px] placeholder:text-[#020202] focus:ring-[#58F2A9]"
                }`}
              />

              <span className="text-[#878DA1] text-[9.8px]">
                Add a CAPI token to improve tracking accuracy (optional)
              </span>
            </div>

            <PixelCapiHowTo
              isOpen={isHowToOpen}
              onToggle={onToggleHowTo}
              action={
                <button
                  type="button"
                  onClick={() => {
                    if (isSaveDisabled) return;
                    onSave();
                  }}
                  disabled={isSaveDisabled}
                  /* ✅ FIX: w-full on mobile (matches screenshot), fixed width only on sm+ */
                  className="w-full sm:w-[224px] h-[44px] sm:h-[37px] px-6 sm:px-8 flex items-center justify-center gap-2 rounded-full text-[14px] font-medium text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                  style={{
                    background: isSaveDisabled
                      ? "linear-gradient(0deg, rgba(135, 141, 161, 0.18) 0%, rgba(0, 195, 240, 0.35) 100%)"
                      : "linear-gradient(0deg, rgba(135, 141, 161, 0.3) 0%, #00C3F0 100%)",
                    fontFamily: "Poppins",
                  }}
                >
                  {isSaving ? (
                    <CircularProgress size={16} style={{ color: "#ffffff" }} />
                  ) : (
                    <>
                      Save for future use
                      <img src={addStream} className="w-[20px] h-[20px]" />
                    </>
                  )}
                </button>
              }
            />
          </>
        )}
      </div>
    </div>
  );
}
